import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { notificationService } from '../../services/api';
import BellIcon from '../../assets/icons/BellIcon';
import NotificationItem from './NotificationItem';
import useOpenNotification from './useOpenNotification';
import './NotificationBell.css';

const DROPDOWN_SIZE = 10;

const NotificationBell = ({ variant = 'desktop' }) => {
    const { user } = useAuth();
    const { unreadCount, version, markAllRead } = useNotifications();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const openNotification = useOpenNotification();
    const [isOpen, setIsOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);
    const isDesktop = variant === 'desktop';

    const load = useCallback(async () => {
        try {
            const { data } = await notificationService.getList(null, DROPDOWN_SIZE);
            setItems(data.items);
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) load();
    }, [isOpen, version, load]);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!isOpen) return;

        const handleOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [isOpen]);

    if (!user) return null;

    const handleToggle = () => {
        if (!isDesktop) {
            navigate('/notifications');
            return;
        }
        if (!isOpen) setLoading(true);
        setIsOpen(open => !open);
    };

    const handleOpenItem = (notification) => {
        openNotification(notification);
        setItems(prev => prev.map(i => (i.id === notification.id ? { ...i, isRead: true } : i)));
    };

    const handleMarkAll = async () => {
        await markAllRead();
        setItems(prev => prev.map(i => ({ ...i, isRead: true })));
    };

    return (
        <div className={`notification-bell notification-bell--${variant}`} ref={wrapperRef}>
            <button
                type="button"
                className="notification-bell__button"
                data-testid={`notification-bell-${variant}`}
                onClick={handleToggle}
                aria-label={t('notifications.title')}
            >
                <BellIcon className="notification-bell__icon" />
                {unreadCount > 0 && (
                    <span className="notification-bell__badge" data-testid="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {isDesktop && isOpen && (
                <div className="notification-dropdown" data-testid="notification-dropdown">
                    <div className="notification-dropdown__header">
                        <span className="notification-dropdown__title">{t('notifications.title')}</span>
                        {unreadCount > 0 && (
                            <button type="button" className="notification-dropdown__action" data-testid="notification-mark-all" onClick={handleMarkAll}>
                                {t('notifications.mark_all_read')}
                            </button>
                        )}
                    </div>

                    <div className="notification-dropdown__list">
                        {loading && items.length === 0 ? (
                            <div className="notification-dropdown__empty">{t('common.loading')}</div>
                        ) : items.length === 0 ? (
                            <div className="notification-dropdown__empty">{t('notifications.empty')}</div>
                        ) : (
                            items.map(n => (
                                <NotificationItem key={n.id} notification={n} onOpen={handleOpenItem} />
                            ))
                        )}
                    </div>

                    <button
                        type="button"
                        className="notification-dropdown__footer"
                        data-testid="notification-view-all"
                        onClick={() => navigate('/notifications')}
                    >
                        {t('notifications.view_all')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
